import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Plus, MoreVertical, Edit, Trash2, DollarSign, User, Calendar,
  Phone, Mail, ArrowRight, X, Building2, GripVertical, Eye, Users, Printer
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STAGES = [
  { id: 'lead', label: 'Lead', color: 'bg-gray-500', textColor: 'text-gray-700 dark:text-gray-300', bgColor: 'bg-gray-50 dark:bg-gray-800' },
  { id: 'qualified', label: 'Qualified', color: 'bg-blue-500', textColor: 'text-blue-700 dark:text-blue-300', bgColor: 'bg-blue-50 dark:bg-blue-900/30' },
  { id: 'proposal', label: 'Proposal', color: 'bg-yellow-500', textColor: 'text-yellow-700 dark:text-yellow-300', bgColor: 'bg-yellow-50 dark:bg-yellow-900/30' },
  { id: 'negotiation', label: 'Negotiation', color: 'bg-purple-500', textColor: 'text-purple-700 dark:text-purple-300', bgColor: 'bg-purple-50 dark:bg-purple-900/30' },
  { id: 'won', label: 'Won', color: 'bg-green-500', textColor: 'text-green-700 dark:text-green-300', bgColor: 'bg-green-50 dark:bg-green-900/30' },
  { id: 'lost', label: 'Lost', color: 'bg-red-500', textColor: 'text-red-700 dark:text-red-300', bgColor: 'bg-red-50 dark:bg-red-900/30' },
];

const OpportunitiesPage = () => {
  const { token } = useAuth();
  const [kanbanData, setKanbanData] = useState({});
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [convertType, setConvertType] = useState('customer');
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    value: 0,
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    contact_company: '',
    source: '',
    notes: ''
  });
  const [convertData, setConvertData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company: '',
    name: '',
    description: ''
  });
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    fetchKanban();
    fetchStats();
  }, []);

  const fetchKanban = async () => {
    try {
      const response = await fetch(`${API_URL}/api/opportunities/kanban`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setKanbanData(data);
      }
    } catch (error) {
      console.error('Error fetching kanban:', error);
      toast.error('Failed to load opportunities');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/opportunities/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleDragStart = (e, opportunity, sourceStage) => {
    setDraggedItem({ opportunity, sourceStage });
    e.dataTransfer.effectAllowed = 'move';
    e.currentTarget.classList.add('opacity-50');
  };

  const handleDragEnd = (e) => {
    e.currentTarget.classList.remove('opacity-50');
    setDraggedItem(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetStage) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.sourceStage === targetStage) return;

    const { opportunity, sourceStage } = draggedItem;

    // Optimistic update
    setKanbanData(prev => {
      const newData = { ...prev };
      newData[sourceStage] = prev[sourceStage].filter(o => o.id !== opportunity.id);
      newData[targetStage] = [...(prev[targetStage] || []), { ...opportunity, stage: targetStage }];
      return newData;
    });

    try {
      const response = await fetch(`${API_URL}/api/opportunities/${opportunity.id}/stage`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          stage: targetStage,
          position: kanbanData[targetStage]?.length || 0
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update stage');
      }

      fetchStats();
      toast.success(`Moved to ${STAGES.find(s => s.id === targetStage)?.label}`);
    } catch (error) {
      // Revert on error
      fetchKanban();
      toast.error('Failed to move opportunity');
    }
  };

  const handleCreate = async () => {
    try {
      const response = await fetch(`${API_URL}/api/opportunities/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ...formData, stage: 'lead' })
      });

      if (response.ok) {
        toast.success('Opportunity created');
        setShowCreateModal(false);
        resetForm();
        fetchKanban();
        fetchStats();
      } else {
        toast.error('Failed to create opportunity');
      }
    } catch (error) {
      toast.error('Failed to create opportunity');
    }
  };

  const handleUpdate = async () => {
    try {
      const response = await fetch(`${API_URL}/api/opportunities/${selectedOpportunity.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Opportunity updated');
        setShowCreateModal(false);
        setIsEditing(false);
        resetForm();
        fetchKanban();
      } else {
        toast.error('Failed to update opportunity');
      }
    } catch (error) {
      toast.error('Failed to update opportunity');
    }
  };

  const handleDelete = async (opportunityId) => {
    if (!window.confirm('Are you sure you want to delete this opportunity?')) return;

    try {
      const response = await fetch(`${API_URL}/api/opportunities/${opportunityId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Opportunity deleted');
        fetchKanban();
        fetchStats();
      } else {
        toast.error('Failed to delete opportunity');
      }
    } catch (error) {
      toast.error('Failed to delete opportunity');
    }
  };

  const handleDownloadPdf = async (opportunityId) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/opportunities/${opportunityId}/pdf`,
        {
          headers: { 'Authorization': `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `FusionBuilderPro_Quote_${opportunityId.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Quote PDF downloaded!');
    } catch (error) {
      console.error('Failed to download PDF:', error);
      toast.error('Failed to download PDF');
    }
  };

  const handleConvert = async () => {
    try {
      const endpoint = convertType === 'customer' 
        ? `${API_URL}/api/opportunities/${selectedOpportunity.id}/convert-to-customer`
        : `${API_URL}/api/opportunities/${selectedOpportunity.id}/convert-to-project`;

      const body = convertType === 'customer' 
        ? {
            first_name: convertData.first_name,
            last_name: convertData.last_name,
            email: convertData.email,
            phone: convertData.phone,
            company: convertData.company
          }
        : {
            name: convertData.name,
            description: convertData.description
          };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        toast.success(`Converted to ${convertType}`);
        setShowConvertModal(false);
        setShowDetailModal(false);
        fetchKanban();
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Conversion failed');
      }
    } catch (error) {
      toast.error('Conversion failed');
    }
  };

  const openEditModal = (opp) => {
    setSelectedOpportunity(opp);
    setFormData({
      title: opp.title || '',
      description: opp.description || '',
      value: opp.value || 0,
      contact_name: opp.contact_name || '',
      contact_email: opp.contact_email || '',
      contact_phone: opp.contact_phone || '',
      contact_company: opp.contact_company || '',
      source: opp.source || '',
      notes: opp.notes || ''
    });
    setIsEditing(true);
    setShowCreateModal(true);
  };

  const openConvertModal = (type) => {
    setConvertType(type);
    if (type === 'customer') {
      const nameParts = (selectedOpportunity.contact_name || '').split(' ');
      setConvertData({
        first_name: nameParts[0] || '',
        last_name: nameParts.slice(1).join(' ') || '',
        email: selectedOpportunity.contact_email || '',
        phone: selectedOpportunity.contact_phone || '',
        company: selectedOpportunity.contact_company || ''
      });
    } else {
      setConvertData({
        name: selectedOpportunity.title || '',
        description: selectedOpportunity.description || ''
      });
    }
    setShowConvertModal(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      value: 0,
      contact_name: '',
      contact_email: '',
      contact_phone: '',
      contact_company: '',
      source: '',
      notes: ''
    });
    setSelectedOpportunity(null);
    setIsEditing(false);
  };

  const getStageTotal = (stageId) => {
    return (kanbanData[stageId] || []).reduce((sum, opp) => sum + (opp.value || 0), 0);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Sales Pipeline</h1>
            <p className="text-gray-600 dark:text-gray-300">Drag and drop opportunities to update their stage</p>
          </div>
          <Button onClick={() => { resetForm(); setShowCreateModal(true); }} data-testid="add-opportunity-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Opportunity
          </Button>
        </div>

        {/* Stats Bar */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-gray-600 dark:text-gray-300">Pipeline Value</p>
                <p className="text-xl font-bold">${stats.weighted_pipeline?.toLocaleString()}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">weighted</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-gray-600 dark:text-gray-300">Open Deals</p>
                <p className="text-xl font-bold">{stats.total - (stats.stages?.won || 0) - (stats.stages?.lost || 0)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-gray-600 dark:text-gray-300">Won Revenue</p>
                <p className="text-xl font-bold text-green-600">${stats.total_won_revenue?.toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-gray-600 dark:text-gray-300">Conversion Rate</p>
                <p className="text-xl font-bold">{stats.conversion_rate}%</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Kanban Board */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-600"></div>
          </div>
        ) : (
          <div className="flex gap-4 overflow-x-auto pb-4" data-testid="kanban-board">
            {STAGES.map((stage) => (
              <div
                key={stage.id}
                className="flex-shrink-0 w-80"
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, stage.id)}
                data-testid={`kanban-column-${stage.id}`}
              >
                <div className={`rounded-lg ${stage.bgColor} p-3`}>
                  {/* Column Header */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${stage.color}`}></div>
                      <span className={`font-semibold ${stage.textColor}`}>{stage.label}</span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        ({kanbanData[stage.id]?.length || 0})
                      </span>
                    </div>
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                      ${getStageTotal(stage.id).toLocaleString()}
                    </span>
                  </div>

                  {/* Cards */}
                  <div className="space-y-3 min-h-[200px]">
                    {(kanbanData[stage.id] || []).map((opp) => (
                      <div
                        key={opp.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, opp, stage.id)}
                        onDragEnd={handleDragEnd}
                        className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-3 cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow"
                        data-testid={`opportunity-card-${opp.id}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{opp.title}</p>
                            <p className="text-lg font-bold text-green-600">
                              ${opp.value?.toLocaleString()}
                            </p>
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => {
                                setSelectedOpportunity(opp);
                                setShowDetailModal(true);
                              }}>
                                <Eye className="w-4 h-4 mr-2" /> View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openEditModal(opp)}>
                                <Edit className="w-4 h-4 mr-2" /> Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDownloadPdf(opp.id)}>
                                <Printer className="w-4 h-4 mr-2" /> Download PDF
                              </DropdownMenuItem>
                              {opp.configuration && (
                                <DropdownMenuItem asChild>
                                  <Link to={`/?edit=${opp.id}`}>
                                    <Building2 className="w-4 h-4 mr-2" /> Edit in Configurator
                                  </Link>
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-red-600"
                                onClick={() => handleDelete(opp.id)}
                              >
                                <Trash2 className="w-4 h-4 mr-2" /> Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>

                        {opp.contact_name && (
                          <div className="flex items-center gap-1 text-sm text-gray-600 mt-2">
                            <User className="w-3 h-3" />
                            <span className="truncate">{opp.contact_name}</span>
                          </div>
                        )}

                        {opp.contact_company && (
                          <div className="flex items-center gap-1 text-sm text-gray-500 mt-1">
                            <Building2 className="w-3 h-3" />
                            <span className="truncate">{opp.contact_company}</span>
                          </div>
                        )}

                        <div className="flex items-center justify-between mt-3 pt-2 border-t dark:border-gray-700">
                          <span className="text-xs text-gray-400">
                            {new Date(opp.created_at).toLocaleDateString()}
                          </span>
                          <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-2 py-0.5 rounded">
                            {opp.probability}% prob
                          </span>
                        </div>
                      </div>
                    ))}

                    {(kanbanData[stage.id] || []).length === 0 && (
                      <div className="text-center py-8 text-gray-400 text-sm">
                        Drop opportunities here
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create/Edit Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{isEditing ? 'Edit Opportunity' : 'New Opportunity'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="block text-sm font-medium mb-1">Title *</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., New Building Project - Smith"
                  data-testid="opportunity-title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Value ($)</label>
                <Input
                  type="number"
                  value={formData.value}
                  onChange={(e) => setFormData({ ...formData, value: parseFloat(e.target.value) || 0 })}
                  placeholder="0.00"
                  data-testid="opportunity-value"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Contact Name</label>
                  <Input
                    value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Company</label>
                  <Input
                    value={formData.contact_company}
                    onChange={(e) => setFormData({ ...formData, contact_company: e.target.value })}
                    placeholder="Acme Inc."
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Email</label>
                  <Input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    placeholder="john@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Phone</label>
                  <Input
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Source</label>
                <select
                  className="w-full border rounded-md px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white dark:border-gray-600"
                  value={formData.source}
                  onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                >
                  <option value="">Select source...</option>
                  <option value="website">Website</option>
                  <option value="referral">Referral</option>
                  <option value="social_media">Social Media</option>
                  <option value="advertisement">Advertisement</option>
                  <option value="cold_call">Cold Call</option>
                  <option value="trade_show">Trade Show</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Notes</label>
                <textarea
                  className="w-full border rounded-md px-3 py-2 min-h-[80px] bg-white dark:bg-gray-800 text-gray-900 dark:text-white dark:border-gray-600"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional notes..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
              <Button onClick={isEditing ? handleUpdate : handleCreate} data-testid="save-opportunity-btn">
                {isEditing ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Detail Modal */}
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Opportunity Details</DialogTitle>
            </DialogHeader>
            {selectedOpportunity && (
              <div className="space-y-4 py-4">
                <div>
                  <h3 className="text-xl font-bold">{selectedOpportunity.title}</h3>
                  <p className="text-2xl font-bold text-green-600 mt-1">
                    ${selectedOpportunity.value?.toLocaleString()}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Stage</p>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      STAGES.find(s => s.id === selectedOpportunity.stage)?.bgColor
                    } ${STAGES.find(s => s.id === selectedOpportunity.stage)?.textColor}`}>
                      {STAGES.find(s => s.id === selectedOpportunity.stage)?.label}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Probability</p>
                    <p className="font-medium">{selectedOpportunity.probability}%</p>
                  </div>
                </div>

                {selectedOpportunity.contact_name && (
                  <div className="border-t pt-4">
                    <h4 className="font-medium mb-2">Contact Information</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-400" />
                        <span>{selectedOpportunity.contact_name}</span>
                      </div>
                      {selectedOpportunity.contact_email && (
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-gray-400" />
                          <span>{selectedOpportunity.contact_email}</span>
                        </div>
                      )}
                      {selectedOpportunity.contact_phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4 text-gray-400" />
                          <span>{selectedOpportunity.contact_phone}</span>
                        </div>
                      )}
                      {selectedOpportunity.contact_company && (
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-gray-400" />
                          <span>{selectedOpportunity.contact_company}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {selectedOpportunity.notes && (
                  <div className="border-t pt-4">
                    <h4 className="font-medium mb-2">Notes</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-300">{selectedOpportunity.notes}</p>
                  </div>
                )}

                <div className="border-t pt-4">
                  <h4 className="font-medium mb-2">Convert</h4>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => openConvertModal('customer')}
                      className="flex-1"
                      data-testid="convert-to-customer-btn"
                    >
                      <Users className="w-4 h-4 mr-2" />
                      To Customer
                    </Button>
                    {selectedOpportunity.stage === 'won' && selectedOpportunity.customer_id && (
                      <Button
                        variant="outline"
                        onClick={() => openConvertModal('project')}
                        className="flex-1"
                        data-testid="convert-to-project-btn"
                      >
                        <ArrowRight className="w-4 h-4 mr-2" />
                        To Project
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDetailModal(false)}>Close</Button>
              <Button onClick={() => {
                setShowDetailModal(false);
                openEditModal(selectedOpportunity);
              }}>
                Edit
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Convert Modal */}
        <Dialog open={showConvertModal} onOpenChange={setShowConvertModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                Convert to {convertType === 'customer' ? 'Customer' : 'Project'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {convertType === 'customer' ? (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">First Name *</label>
                      <Input
                        value={convertData.first_name}
                        onChange={(e) => setConvertData({ ...convertData, first_name: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Last Name *</label>
                      <Input
                        value={convertData.last_name}
                        onChange={(e) => setConvertData({ ...convertData, last_name: e.target.value })}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Email *</label>
                    <Input
                      value={convertData.email}
                      onChange={(e) => setConvertData({ ...convertData, email: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Phone</label>
                    <Input
                      value={convertData.phone}
                      onChange={(e) => setConvertData({ ...convertData, phone: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Company</label>
                    <Input
                      value={convertData.company}
                      onChange={(e) => setConvertData({ ...convertData, company: e.target.value })}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-1">Project Name *</label>
                    <Input
                      value={convertData.name}
                      onChange={(e) => setConvertData({ ...convertData, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Description</label>
                    <textarea
                      className="w-full border rounded-md px-3 py-2 min-h-[80px]"
                      value={convertData.description}
                      onChange={(e) => setConvertData({ ...convertData, description: e.target.value })}
                    />
                  </div>
                </>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowConvertModal(false)}>Cancel</Button>
              <Button onClick={handleConvert} data-testid="confirm-convert-btn">
                Convert
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default OpportunitiesPage;
